/**
 * Utility functions for handling post-authentication redirects
 * Implements security measures to prevent open redirect vulnerabilities
 */

// Valid redirect paths that are allowed after authentication
const VALID_REDIRECT_PATHS = [
  '/dashboard',
  '/profile',
  '/profile-setup',
  '/campaigns',
  '/campaigns/create',
  '/discover',
  '/search',
  '/matches',
  '/messages',
];

// Regex patterns for dynamic routes
const VALID_DYNAMIC_PATTERNS = [
  /^\/campaigns\/[a-zA-Z0-9-]+$/, // Campaign details
  /^\/influencers\/[a-zA-Z0-9-]+$/, // Influencer profile
];

// Session storage keys
const REDIRECT_PATH_KEY = 'auth_redirect_path';
const INTENDED_ACTION_KEY = 'auth_intended_action';
const REDIRECT_TIMESTAMP_KEY = 'auth_redirect_timestamp';

// Redirect data expires after 30 minutes
const REDIRECT_EXPIRY_MS = 30 * 60 * 1000;

/**
 * Validates if a redirect path is safe and allowed
 */
export function isValidRedirectPath(path: string): boolean {
  if (!path || typeof path !== 'string') {
    return false;
  }

  // Prevent absolute URLs or protocol-relative URLs (open redirect vulnerability)
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
    return false;
  }

  // Must start with /
  if (!path.startsWith('/')) {
    return false;
  }

  // Check against static paths
  if (VALID_REDIRECT_PATHS.includes(path)) {
    return true;
  }

  // Check against dynamic patterns
  return VALID_DYNAMIC_PATTERNS.some(pattern => pattern.test(path));
}

/**
 * Stores the intended redirect path and action in session storage
 */
export function storeRedirectIntent(redirectPath: string, action?: string): void {
  if (!isValidRedirectPath(redirectPath)) {
    console.warn('Invalid redirect path, not storing:', redirectPath);
    return;
  }

  try {
    sessionStorage.setItem(REDIRECT_PATH_KEY, redirectPath);
    sessionStorage.setItem(REDIRECT_TIMESTAMP_KEY, Date.now().toString());
    
    if (action) {
      sessionStorage.setItem(INTENDED_ACTION_KEY, action);
    }
  } catch (error) {
    console.error('Failed to store redirect intent:', error);
  }
}

/**
 * Retrieves the stored redirect intent if it exists and hasn't expired
 */
export function getRedirectIntent(): { path: string; action?: string } | null {
  try {
    const path = sessionStorage.getItem(REDIRECT_PATH_KEY);
    const timestamp = sessionStorage.getItem(REDIRECT_TIMESTAMP_KEY);
    const action = sessionStorage.getItem(INTENDED_ACTION_KEY);

    // No redirect stored
    if (!path) {
      return null;
    }

    // Check if redirect has expired
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age > REDIRECT_EXPIRY_MS) {
        console.log('Redirect intent expired, clearing');
        clearRedirectIntent();
        return null;
      }
    }

    // Validate the stored path
    if (!isValidRedirectPath(path)) {
      console.warn('Stored redirect path is invalid, clearing:', path);
      clearRedirectIntent();
      return null;
    }

    return {
      path,
      action: action || undefined,
    };
  } catch (error) {
    console.error('Failed to retrieve redirect intent:', error);
    return null;
  }
}

/**
 * Clears all stored redirect intent data
 */
export function clearRedirectIntent(): void {
  try {
    sessionStorage.removeItem(REDIRECT_PATH_KEY);
    sessionStorage.removeItem(INTENDED_ACTION_KEY);
    sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Failed to clear redirect intent:', error);
  }
}

/**
 * Gets the redirect path to use after authentication
 * Returns the stored redirect path if valid, otherwise returns the default path
 */
export function getPostAuthRedirectPath(defaultPath: string = '/dashboard'): string {
  const intent = getRedirectIntent();
  
  if (intent) {
    clearRedirectIntent();
    return intent.path;
  }
  
  return defaultPath;
}
